import { createSelector } from 'reselect';

import { StoreState } from '../../../reducers';
import { IndesignTextFieldsConfig } from '../utils/indesignEditor/editableTextFields/getData';
import { HtmlTextFieldsConfig } from '../utils/htmlEditor/editableTextFields/getData';
import { IndesignMediaElementConfig } from '../utils/indesignEditor/editableMedia/getData';
import { HtmlMediaElementConfig } from '../utils/htmlEditor/editableMedia/getData';
import { LinkConfig } from '../utils/editableLinks/getData';
import { ButtonConfig } from '../utils/indesignEditor/editableButtons/getData';
import { IframeConfig } from '../utils/indesignEditor/editableIframes/getData';
import { EnrichmentConfig } from '../utils/indesignEditor/editableEnrichments/getData';
import { SliderConfig } from '../utils/indesignEditor/editableSliders/getData';
import { ThemedItemConfig } from '../utils/indesignEditor/themedItems/getData';
import {
	EditableTextFieldsState,
	EditableMediaState,
	EditableLinksState,
	EditableButtonsState,
	EditableIframesState,
	EditableEnrichmentsState,
	EditableSlidersState,
	ThemedItemsState,
	DuplicatedItemsInSpreadState,
} from '../reducer';

export type EditableItem =
	IndesignTextFieldsConfig |
	HtmlTextFieldsConfig |
	IndesignMediaElementConfig |
	HtmlMediaElementConfig |
	LinkConfig |
	ButtonConfig |
	IframeConfig |
	EnrichmentConfig |
	SliderConfig |
	ThemedItemConfig;

type EditableItems =
	EditableTextFieldsState |
	EditableMediaState |
	EditableLinksState |
	EditableButtonsState |
	EditableIframesState |
	EditableEnrichmentsState |
	EditableSlidersState |
	ThemedItemsState;

const getCurrentSpread = (state: StoreState): number[] => state.editor.pagesController.spreads[state.editor.pagesController.currentSpreadIndex];
const getPageEditableItems = (
	items: EditableItems,
	page: number,
	duplicatedItemsInSpread?: DuplicatedItemsInSpreadState,
	spread?: number[],
): EditableItem[] => {
	if (!items[page]) {
		return [];
	}

	const pageItems = Object.values(items[page]);

	if (!duplicatedItemsInSpread || !spread) {
		return pageItems;
	}

	const adjacentPage = spread.find(pageInSpread => pageInSpread !== page);

	if (!adjacentPage || !items[adjacentPage]) {
		return pageItems;
	}

	const adjacentPageItems: EditableItem[] = Object.values(items[adjacentPage]);
	const duplicatedItems = Object.values(duplicatedItemsInSpread[page]).reduce((acc, current) => {
		const duplicatedItemData = adjacentPageItems.find(item => (
			'className' in item && !!item.className && item.className.includes(current.className)
		));

		if (duplicatedItemData) {
			acc.push({
				...duplicatedItemData,
				...current,
			});
		}

		return acc;
	}, [] as EditableItem[]);

	return [...pageItems, ...duplicatedItems];
};

const getEditableTextFields = (state: StoreState): EditableTextFieldsState => state.editor.editableTextFields;
const getEditableMedia = (state: StoreState): EditableMediaState => state.editor.editableMedia;
const getEditableLinks = (state: StoreState): EditableLinksState => state.editor.editableLinks;
const getEditableButtons = (state: StoreState): EditableButtonsState => state.editor.editableButtons;
const getEditableIframes = (state: StoreState): EditableIframesState => state.editor.editableIframes;
const getEditableEnrichments = (state: StoreState): EditableEnrichmentsState => state.editor.editableEnrichments;
const getEditableSliders = (state: StoreState): EditableSlidersState => state.editor.editableSliders;
const getThemedItems = (state: StoreState): ThemedItemsState => state.editor.themedItems;
const getDuplicatedItemsInSpread = (state: StoreState): DuplicatedItemsInSpreadState => state.editor.duplicatedItemsInSpread;

export const getPageEditableTextFields = createSelector(
	getEditableTextFields,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableTextFieldsState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableMedia = createSelector(
	getEditableMedia,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableMediaState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableLinks = createSelector(
	getEditableLinks,
	(state: StoreState, page: number): number => page,
	(items: EditableLinksState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableButtons = createSelector(
	getEditableButtons,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableButtonsState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableIframes = createSelector(
	getEditableIframes,
	(state: StoreState, page: number): number => page,
	(items: EditableIframesState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableEnrichments = createSelector(
	getEditableEnrichments,
	(state: StoreState, page: number): number => page,
	(items: EditableEnrichmentsState, page: number): EditableItem[] => getPageEditableItems(items, page),
);

export const getPageEditableSliders = createSelector(
	getEditableSliders,
	getDuplicatedItemsInSpread,
	getCurrentSpread,
	(state: StoreState, page: number): number => page,
	(items: EditableSlidersState, duplicatedItemsInSpread: DuplicatedItemsInSpreadState, spread: number[], page: number): EditableItem[] => (
		getPageEditableItems(items, page, duplicatedItemsInSpread, spread)
	),
);

export const getPageEditableTextFieldsIds = createSelector(
	getEditableTextFields,
	(state: StoreState, page: number): number => page,
	(items: EditableTextFieldsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableMediaIds = createSelector(
	getEditableMedia,
	(state: StoreState, page: number): number => page,
	(items: EditableMediaState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableLinksIds = createSelector(
	getEditableLinks,
	(state: StoreState, page: number): number => page,
	(items: EditableLinksState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableButtonsIds = createSelector(
	getEditableButtons,
	(state: StoreState, page: number): number => page,
	(items: EditableButtonsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableIframesIds = createSelector(
	getEditableIframes,
	(state: StoreState, page: number): number => page,
	(items: EditableIframesState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableEnrichmentsIds = createSelector(
	getEditableEnrichments,
	(state: StoreState, page: number): number => page,
	(items: EditableEnrichmentsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageEditableSlidersIds = createSelector(
	getEditableSliders,
	(state: StoreState, page: number): number => page,
	(items: EditableSlidersState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);

export const getPageThemedItemsIds = createSelector(
	getThemedItems,
	(state: StoreState, page: number): number => page,
	(items: ThemedItemsState, page: number): string[] => getPageEditableItems(items, page).map(item => item.id),
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/selectors/editableItems.ts